import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import { ICreatedInstanceId, IGcpProjectId } from 'src/app/utils/IFactory';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { PromiseCancellationToken } from 'src/app/utils/promise/cancellation-token';
import { buildWhereSQL } from 'src/app/utils/query/sql';
import { hashCode } from 'src/app/utils/string-hash';
import {
    DataLoaderResult,
    IDataLoaderResult,
    mergeDataLoaderResultStatus,
} from '../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../services/data-loader/utils/standard-run';
import { DatasourceColumnHelper } from './datasource-column-helper';
import { SqlComposer } from 'src/app/utils/query/sql-composer';

export interface DatasourceColumnDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
    datasourceColumnId: DatasourceColumnId;
    datasourceSqlMap: IDatasourceSQLMap | IDatasourceSQLMap[];
    /** Add where statement based on queryContext. Default false. One can also pass an array of columnIds to limit the context filter */
    filterContext?: boolean | DatasourceColumnId[];
    /** Ignore columns from queryContext
     * @warning If filterContext is set as an array, then ignoredContextColumnIds values are ignored
     */
    ignoredContextColumnIds?: DatasourceColumnId[];
    /** Subquery to order rows. In query reference to column `value` */
    orderByQuery?: string | undefined;
    /** An array of datasourceColumnIds present in datasourceSqlMap to order by */
    orderByDatasourceColumnIds?: DatasourceColumnId[];
    /** Use this option if you want to add additional sql token */
    additionalSQLTokens?: Partial<ICompiledSQLTokens>;
    /** Dev mode, for testing purposes only */
    devMode?: {
        /** Wait time in ms before executing the query */
        wait?: number;
    };
}

export class DataSourceColumnDataLoaderFactory extends Factory<
    DataLoader,
    DatasourceColumnDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'DataSourceColumnDataLoaderFactory';

    override getInstance(
        options: DatasourceColumnDataLoaderFactoryOptions
    ): DataLoader {
        // resolve default options
        const finalOptions = {
            ...options,
            filterContext: options.filterContext ?? false,
            ignoredContextColumnIds: options.ignoredContextColumnIds ?? [
                'ACTIVITY_APP_VERSION',
            ],
        };

        return new DataLoader(finalOptions.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            await DataSourceColumnDataLoaderFactory.executeQueryContext(
                this,
                queryContext,
                cancellationToken,
                finalOptions
            );
        });
    }

    private static async executeQueryContext(
        instance: DataLoader,
        queryContext: IQueryContext,
        cancellationToken: PromiseCancellationToken,
        options: DatasourceColumnDataLoaderFactoryOptions
    ) {
        const datasourceColumnId: DatasourceColumnId =
            options.datasourceColumnId;

        // make sure to not modify original queryContext
        queryContext = structuredClone(queryContext);

        // filter from queryContext columns which are present but isFilterApplied is not true
        // this dataloader is allowed to only filter data so other features like splits should be ignored
        queryContext.datasourceColumns = queryContext.datasourceColumns.filter(
            (col) => col.state.isFilterApplied == true
        );

        if (
            options.orderByQuery &&
            options.orderByDatasourceColumnIds !== undefined
        ) {
            throw new Error(
                `Both orderByQuery and orderByQueryGetter are defined in DataSourceColumnDataLoaderFactory options for datasourceColumnId ${datasourceColumnId}. Please define only one of them.`
            );
        }

        // Helper to build a SELECT query for a given sqlMap
        const buildSelectQuery = (datasourceSqlMap: IDatasourceSQLMap) => {
            // identify columns not supported in datasource
            const dsColumns = DataLoader.separatePresentAndMissingDims(
                queryContext.datasourceColumns,
                datasourceSqlMap.columns
            );

            const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                DataLoader.getMissingColumnsStatus(dsColumns.missing);

            // The column this loader fetches values for must exist in this map. In the
            // array/union form a given map may legitimately not contain it — skip that map
            // (the other maps still contribute values). We warn (with the filter's
            // instanceId + table) so the offending filter is identifiable, instead of
            // crashing with a cryptic "Cannot read properties of undefined (reading
            // 'columnSQL')".
            const queriedColumn = datasourceSqlMap.columns[datasourceColumnId];
            if (!queriedColumn) {
                console.warn(
                    `[${instance.instanceId}] DataSourceColumnDataLoaderFactory: queried column '${datasourceColumnId}' is not present in datasourceSqlMap '${datasourceSqlMap.dataset}.${datasourceSqlMap.tableId}' — skipping this map.`
                );
                return null;
            }

            const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
                dsColumns.present
                    // filter out queried column, because we don't want to filter by itself
                    .filter(
                        (col: IQueryContextDatasourceColumn) =>
                            col.id != datasourceColumnId
                    )
                    // context filter functionality
                    .filter((col: IQueryContextDatasourceColumn) =>
                        DatasourceColumnHelper.filterContextFn(col, {
                            filterContext: options.filterContext,
                            ignoredContextColumnIds:
                                options.ignoredContextColumnIds,
                        })
                    )
                    .map((dsCol) => {
                        const contextColumn = datasourceSqlMap.columns[dsCol.id];
                        if (!contextColumn?.compileSQLTokens) {
                            throw new Error(
                                `[${instance.instanceId}] filter '${datasourceColumnId}': context column '${dsCol.id}' has no compileSQLTokens in datasourceSqlMap '${datasourceSqlMap.dataset}.${datasourceSqlMap.tableId}'.`
                            );
                        }
                        return contextColumn.compileSQLTokens(dsCol.state, options);
                    });

            // push additional SQLTokens
            sqlTokensArray.push(options.additionalSQLTokens || {});

            // merge tokens into single deduplicated object of type ICompiledSQLTokens
            const sqlTokens: ICompiledSQLTokens =
                mergeSqlTokensArray(sqlTokensArray);

            const columnSelect = queriedColumn.columnSQL;
            // const columnAlias = datasourceColumnId.replace('.', '_');
            const unnestDatasourceColumn =
                sqlTokens.unnestDatasourceColumn.join('\n');
            const where: string = buildWhereSQL(sqlTokens.where);
            const join: string = sqlTokens.join.join('\n');

            const orderByDatasourceColumns = (() => {
                // if orderByDatasourceColumnIds is defined, then add them to SELECT statement to order by those columns
                if (options.orderByDatasourceColumnIds !== undefined) {
                    const select: string[] = [];
                    const orderBy: string[] = [];
                    for (const dsColumnId of options.orderByDatasourceColumnIds) {
                        const dsColumn = datasourceSqlMap.columns[dsColumnId];
                        if (dsColumn === undefined) {
                            throw new Error(
                                `orderByDatasourceColumnIds contains a columnId ${dsColumnId} which is not present in datasourceSqlMap for datasourceColumnId ${datasourceColumnId}.`
                            );
                        }
                        select.push(`${dsColumn.columnSQL} AS ${dsColumnId}`);
                        orderBy.push(`${dsColumnId}`);
                    }
                    return { select, orderBy };
                }
                return { select: [], orderBy: [] };
            })();

            const orderByExpr = (() => {
                //
                if (options.orderByDatasourceColumnIds !== undefined) {
                    return options.orderByDatasourceColumnIds.map(
                        (colId) => `${colId}`
                    );
                }
                return [options.orderByQuery ?? 'value'];
            })();

            return {
                query: `
${SqlComposer.SELECT([
    `DISTINCT ${columnSelect} AS value`,
    `COALESCE(CAST(${columnSelect} AS STRING), 'N/A') AS label`,
    ...orderByDatasourceColumns.select,
])}
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${unnestDatasourceColumn}
${join}
${where}
${SqlComposer.ORDER_BY(orderByExpr)}
        `.trim(),

                pendingDataLoaderResultStatus,
                pendingStatusMessage,
            };
        };

        const process = async () => {
            instance.emitter.emit(
                DataLoaderResult.getLoading(instance.latestData, queryContext)
            );
            if (options.devMode?.wait) {
                await new Promise((resolve) =>
                    setTimeout(resolve, options.devMode!.wait!)
                );
            }

            // Support array or single object for datasourceSqlMap
            const sqlMaps = Array.isArray(options.datasourceSqlMap)
                ? options.datasourceSqlMap
                : [options.datasourceSqlMap];

            // Build queries for each map, dropping maps that don't expose the queried column
            const queries = sqlMaps
                .map(buildSelectQuery)
                .filter((q): q is NonNullable<typeof q> => q !== null);

            // The queried column was not present in ANY provided map — a real
            // misconfiguration (the filter references a column no datasource exposes).
            // Throw with full context so the offending filter is obvious in the UI/console.
            if (queries.length === 0) {
                const tables = sqlMaps
                    .map((m) => `${m.dataset}.${m.tableId}`)
                    .join(', ');
                throw new Error(
                    `[${instance.instanceId}] DataSourceColumnDataLoaderFactory: queried column '${datasourceColumnId}' not found in any datasourceSqlMap (${tables}). Check the filterDataLoaders wiring for this filter.`
                );
            }
            // if only one query, return it directly
            const finalQuery = (() => {
                if (queries.length === 1) {
                    return `
-- DataLoaderId : ${instance.instanceId}
${queries[0].query}`;
                }
                // Union all queries
                const unionQuery = queries
                    .map((q) => `(${q.query})`)
                    .join('\nUNION ALL\n');

                const selectDatasourceColumns =
                    options.orderByDatasourceColumnIds !== undefined
                        ? options.orderByDatasourceColumnIds.map(
                              (colId) => `${colId}`
                          )
                        : [];
                const orderByExpr = (() => {
                    //
                    if (options.orderByDatasourceColumnIds !== undefined) {
                        return options.orderByDatasourceColumnIds.map(
                            (colId) => `${colId}`
                        );
                    }
                    return [options.orderByQuery ?? 'value'];
                })();

                // Final query: select distinct from union
                return `
-- DataLoaderId : ${instance.instanceId}
${SqlComposer.SELECT(['DISTINCT value', 'label', ...selectDatasourceColumns])}
FROM (
${unionQuery}
) AS unioned
${SqlComposer.ORDER_BY(orderByExpr)}
        `.trim();
            })();

            const queryHash = hashCode(finalQuery);
            if (instance.latestQueryHash !== undefined) {
                if (instance.latestQueryHash === queryHash) return;
                // cancel only if we have data from previous query
                cancellationToken.throwIfCancellationRequested();
            }

            const queryResponse = await DataLoader.runQuery(
                finalQuery,
                options.queryCacheService
            );
            const data = [...queryResponse.result];
            instance.latestQueryHash = queryHash;
            instance.latestData = structuredClone(data);

            let dl: IDataLoaderResult = {
                data,
                queryContext,
                status: mergeDataLoaderResultStatus(
                    queries.map((q) => q.pendingDataLoaderResultStatus)
                ),
                statusMessage: queries
                    .map((q) => q.pendingStatusMessage)
                    .join('\n'),
                isLoading: false,
                sqlAndJsProcessing: finalQuery,
            };
            instance.emitter.emit(dl);
        };

        await standardRun(instance, queryContext, process);
    }
}
